import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";

import CashEntryServices from "../../../services/CashEntryServices";
import CashReturnedCashInEditModal from "./CashReturnedCashInEditModal";
import CashReturnedCashOutEditModal from "./CashReturnedCashOutEditModal";

const readEntries = (response) => {
  const body = response?.data?.status
    ? response.data
    : response;

  if (!Array.isArray(body?.data?.entries)) {
    throw new Error("Invalid cash entries response.");
  }

  return body.data.entries;
};

const CashReturnedRejectedModal = ({
  show,
  onHide,
  kind = "returned",
  userRole = "",
  onChanged
}) => {
  const [entryType, setEntryType] = useState("out");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingEntry, setEditingEntry] = useState(null);
  const [actionEntry, setActionEntry] = useState(null);
  const [action, setAction] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const actionLock = useRef(false);

  const isReturned = kind === "returned";
  const isMaker =
    String(userRole).trim().toLowerCase() === "cashmaker";

  useEffect(() => {
    if (!show) return;

    setEntryType("out");
    setEditingEntry(null);
    setActionEntry(null);
    setAction("");
    setReason("");
    setError("");
  }, [show, kind]);

  useEffect(() => {
    if (!show) return;

    let active = true;

    setLoading(true);
    setError("");
    setEntries([]);

    const statuses = isReturned
      ? ["returnedToMaker"]
      : ["rejectedByAdmin", "rejectedByAccounts"];

    Promise.all(
      statuses.map((status) =>
        CashEntryServices.getCashEntries({
          entryType,
          status,
          pendingOnly: false
        })
      )
    )
      .then((responses) => {
        if (!active) return;

        const rows = responses.flatMap(readEntries);

        setEntries(
          rows.filter(
            (entry) =>
              entry.entryType === entryType &&
              statuses.includes(entry.status) &&
              (isReturned || entry.rejectionAccepted !== true)
          )
        );
      })
      .catch((requestError) => {
        if (!active) return;

        setError(
          requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to load entries."
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [show, entryType, isReturned, refreshKey]);

  const refreshAfterChange = () => {
    setRefreshKey((value) => value + 1);
    onChanged?.();
  };

  const closeModal = () => {
    if (!actionLock.current) onHide();
  };

  const openAction = (entry, nextAction) => {
    if (!isMaker || actionLock.current) return;

    setActionEntry(entry);
    setAction(nextAction);
    setReason("");
    setError("");
  };

  const submitAction = async () => {
    if (!isMaker || !actionEntry || actionLock.current) return;

    if (action === "reprocess" && !reason.trim()) {
      setError("Please enter a reprocessing reason.");
      return;
    }

    actionLock.current = true;
    setBusy(true);
    setError("");

    try {
      if (action === "accept") {
        await CashEntryServices.acceptRejection(actionEntry._id);
      } else {
        await CashEntryServices.reprocessRejectedEntry(
          actionEntry._id,
          { reason: reason.trim() }
        );
      }

      setActionEntry(null);
      setAction("");
      setReason("");
      refreshAfterChange();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
        requestError?.message ||
        "Unable to complete this action."
      );
    } finally {
      actionLock.current = false;
      setBusy(false);
    }
  };

  const EditModal =
    editingEntry?.entryType === "out"
      ? CashReturnedCashOutEditModal
      : CashReturnedCashInEditModal;

  return (
    <>
      <Modal
        show={show && !editingEntry}
        onHide={closeModal}
        size="xl"
        fullscreen="lg-down"
        scrollable
        centered
        backdrop={busy ? "static" : true}
        keyboard={!busy}
      >
        <Modal.Header closeButton={!busy}>
          <Modal.Title>
            {isReturned
              ? "Returned — Correction Required"
              : "Rejected — Accept / Reprocess"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="erp-compact">
          <div className="d-flex gap-2 mb-3">
            {[
              { value: "out", label: "Cash Out" },
              { value: "in", label: "Cash In" }
            ].map((tab) => (
              <button
                key={tab.value}
                type="button"
                className={`btn ${
                  entryType === tab.value
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                disabled={busy || Boolean(actionEntry)}
                onClick={() => setEntryType(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {!isMaker && (
            <div className="alert alert-info py-2">
              View only. CashMaker can take action on these entries.
            </div>
          )}

          {error && (
            <div className="alert alert-danger py-2" role="alert">
              {error}
            </div>
          )}

          {actionEntry && (
            <div className="border rounded p-3 mb-3">
              <h6>
                {action === "accept"
                  ? "Accept Rejection"
                  : "Reprocess Entry"}{" "}
                — {actionEntry.requestNumber || "Selected entry"}
              </h6>

              {action === "accept" ? (
                <p>
                  The entry will remain rejected and its amount will
                  be excluded from projection.
                </p>
              ) : (
                <>
                  <p>
                    This entry will be sent to{" "}
                    {actionEntry.entryType === "out"
                      ? "CashChecker"
                      : "Accounts"}{" "}
                    for approval again.
                  </p>

                  <label htmlFor="cash-reprocess-reason">
                    Reprocessing reason
                  </label>

                  <textarea
                    id="cash-reprocess-reason"
                    className="form-control mb-3"
                    rows={3}
                    value={reason}
                    disabled={busy}
                    onChange={(event) => setReason(event.target.value)}
                  />
                </>
              )}

              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={submitAction}
                >
                  {busy
                    ? "Please wait…"
                    : action === "accept"
                      ? "Confirm Acceptance"
                      : "Send for Approval"}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={busy}
                  onClick={() => {
                    setActionEntry(null);
                    setError("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="table-responsive">
            <table className="table table-bordered align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Request / Remark</th>
                  <th>Passbook / Store</th>
                  <th>Entry Date</th>
                  <th>Amount</th>
                  <th>
                    {isReturned
                      ? "Returned From / Reason"
                      : "Rejected By / Remark"}
                  </th>
                  {isMaker && <th>Actions</th>}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={isMaker ? 7 : 6}>
                      Loading entries…
                    </td>
                  </tr>
                ) : entries.length === 0 ? (
                  <tr>
                    <td colSpan={isMaker ? 7 : 6}>
                      No entries found.
                    </td>
                  </tr>
                ) : (
                  entries.map((entry, index) => (
                    <tr key={entry._id}>
                      <td>{index + 1}</td>

                      <td>
                        <strong>{entry.requestNumber || "—"}</strong>
                        <div>{entry.remark || "—"}</div>
                      </td>

                      <td>
                        <div>{entry.passbook?.name || "—"}</div>
                        <div>{entry.store?.name || "—"}</div>
                      </td>

                      <td>
                        {entry.dateTime
                          ? new Date(entry.dateTime).toLocaleString(
                              "en-IN",
                              { timeZone: "Asia/Kolkata" }
                            )
                          : "—"}
                      </td>

                      <td>
                        ₹{Number(entry.amount || 0).toLocaleString(
                          "en-IN",
                          { minimumFractionDigits: 2 }
                        )}
                      </td>

                      <td>
                        <div>
                          {isReturned
                            ? entry.returnedFrom || "—"
                            : entry.status === "rejectedByAdmin"
                              ? "CashChecker"
                              : "Accounts"}
                        </div>
                        <div>
                          {isReturned
                            ? entry.returnReason || "—"
                            : entry.status === "rejectedByAdmin"
                              ? entry.adminRemark || "—"
                              : entry.accountsRemark || "—"}
                        </div>
                      </td>

                      {isMaker && (
                        <td>
                          {isReturned ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              disabled={busy || Boolean(actionEntry)}
                              onClick={() => setEditingEntry(entry)}
                            >
                              Correct / Resubmit
                            </button>
                          ) : (
                            <div className="d-flex flex-wrap gap-2">
                              <button
                                type="button"
                                className="btn btn-sm btn-success"
                                disabled={busy || Boolean(actionEntry)}
                                onClick={() => openAction(entry, "accept")}
                              >
                                Accept Rejection
                              </button>

                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                disabled={busy || Boolean(actionEntry)}
                                onClick={() => openAction(entry, "reprocess")}
                              >
                                Reprocess
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={busy}
            onClick={closeModal}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {show && isMaker && editingEntry && (
        <EditModal
          show
          entry={editingEntry}
          onHide={() => setEditingEntry(null)}
          onSaved={refreshAfterChange}
        />
      )}
    </>
  );
};

export default CashReturnedRejectedModal;